import { createContext, useContext, useState, ReactNode } from "react";

export type Language = "en" | "tr" | "de";

interface Translations {
  [key: string]: {
    en: string;
    tr: string;
    de: string;
  };
}

export const translations: Translations = {
  // Header
  signIn: {
    en: "Sign In",
    tr: "Giriş Yap",
    de: "Anmelden",
  },
  signOut: {
    en: "Sign Out",
    tr: "Çıkış Yap",
    de: "Abmelden",
  },
  myStories: {
    en: "My Stories",
    tr: "Hikayelerim",
    de: "Meine Geschichten",
  },
  
  // Hero
  heroTitle1: {
    en: "Create Magical Stories",
    tr: "Sihirli Hikayeler Yarat",
    de: "Erstelle magische Geschichten",
  },
  heroTitle2: {
    en: "For Little Dreamers",
    tr: "Küçük Hayalperestler İçin",
    de: "Für kleine Träumer",
  },
  heroDescription: {
    en: "Pick your characters, choose a world, and let AI create a beautiful illustrated story tailored to your child's age and interests.",
    tr: "Karakterlerini seç, bir dünya seç ve yapay zekanın çocuğunuzun yaşına ve ilgi alanlarına uygun güzel bir resimli hikaye oluşturmasına izin verin.",
    de: "Wähle deine Charaktere, wähle eine Welt und lass KI eine wunderschöne illustrierte Geschichte erstellen, die auf das Alter und die Interessen deines Kindes zugeschnitten ist.",
  },
  signInToSave: {
    en: "Sign in to save your custom characters, worlds, and themes!",
    tr: "Özel karakterlerinizi, dünyalarınızı ve temalarınızı kaydetmek için giriş yapın!",
    de: "Melde dich an, um deine benutzerdefinierten Charaktere, Welten und Themen zu speichern!",
  },
  
  // Story Builder
  characters: {
    en: "Characters",
    tr: "Karakterler",
    de: "Charaktere",
  },
  selectCharacters: {
    en: "Select your characters",
    tr: "Karakterlerini seç",
    de: "Wähle deine Charaktere",
  },
  chooseUpTo: {
    en: "Choose up to 3 characters for your story",
    tr: "Hikayeniz için en fazla 3 karakter seçin",
    de: "Wähle bis zu 3 Charaktere für deine Geschichte",
  },
  createCustomCharacter: {
    en: "Create Custom Character",
    tr: "Özel Karakter Oluştur",
    de: "Benutzerdefinierten Charakter erstellen",
  },
  
  world: {
    en: "World",
    tr: "Dünya",
    de: "Welt",
  },
  selectWorld: {
    en: "Select your world",
    tr: "Dünyasını seç",
    de: "Wähle deine Welt",
  },
  whereAdventure: {
    en: "Where will the adventure take place?",
    tr: "Macera nerede geçecek?",
    de: "Wo wird das Abenteuer stattfinden?",
  },
  createCustomWorld: {
    en: "Create Custom World",
    tr: "Özel Dünya Oluştur",
    de: "Benutzerdefinierte Welt erstellen",
  },
  
  theme: {
    en: "Theme",
    tr: "Tema",
    de: "Thema",
  },
  selectTheme: {
    en: "Select a theme",
    tr: "Bir tema seç",
    de: "Wähle ein Thema",
  },
  lessonToLearn: {
    en: "What lesson should the story teach?",
    tr: "Hikaye hangi dersi öğretmeli?",
    de: "Welche Lektion soll die Geschichte lehren?",
  },
  createCustomTheme: {
    en: "Create Custom Theme",
    tr: "Özel Tema Oluştur",
    de: "Benutzerdefiniertes Thema erstellen",
  },
  
  ageGroup: {
    en: "Age Group",
    tr: "Yaş Grubu",
    de: "Altersgruppe",
  },
  selectAgeGroup: {
    en: "Select age group",
    tr: "Yaş grubunu seç",
    de: "Wähle die Altersgruppe",
  },
  tailorContent: {
    en: "We'll tailor the story content and vocabulary",
    tr: "Hikaye içeriğini ve kelime dağarcığını uyarlayacağız",
    de: "Wir passen den Inhalt und das Vokabular der Geschichte an",
  },
  
  createStory: {
    en: "Create My Story",
    tr: "Hikayemi Oluştur",
    de: "Meine Geschichte erstellen",
  },
  selectAtLeastOne: {
    en: "Please select at least one character",
    tr: "Lütfen en az bir karakter seçin",
    de: "Bitte wähle mindestens einen Charakter",
  },
  
  // High Consistency Mode
  highConsistencyMode: {
    en: "High Consistency Mode",
    tr: "Yüksek Tutarlılık Modu",
    de: "Hoher Konsistenzmodus",
  },
  highConsistencyModeDesc: {
    en: "Better character consistency but slower generation (uses panel 1 as style reference)",
    tr: "Daha iyi karakter tutarlılığı ama daha yavaş oluşturma (1. paneli stil referansı olarak kullanır)",
    de: "Bessere Charakterkonsistenz, aber langsamere Generierung (verwendet Panel 1 als Stilreferenz)",
  },
  
  // Story Viewer
  pageOf: {
    en: "Page {current} of {total}",
    tr: "Sayfa {current} / {total}",
    de: "Seite {current} von {total}",
  },
  previous: {
    en: "Previous",
    tr: "Önceki",
    de: "Zurück",
  },
  next: {
    en: "Next",
    tr: "Sonraki",
    de: "Weiter",
  },
  goToMainPage: {
    en: "Main Page",
    tr: "Ana Sayfa",
    de: "Hauptseite",
  },
  createAnotherStory: {
    en: "Create Another Story",
    tr: "Başka Hikaye Oluştur",
    de: "Weitere Geschichte erstellen",
  },
  downloadPdf: {
    en: "Download PDF",
    tr: "PDF İndir",
    de: "PDF herunterladen",
  },
  shareStory: {
    en: "Share Story",
    tr: "Hikayeyi Paylaş",
    de: "Geschichte teilen",
  },
  linkCreated: {
    en: "Link Created",
    tr: "Link Oluşturuldu",
    de: "Link erstellt",
  },
  creatingLink: {
    en: "Creating Link...",
    tr: "Link Oluşturuluyor...",
    de: "Link wird erstellt...",
  },
  regenerateImage: {
    en: "Regenerate Image",
    tr: "Resmi Yenile",
    de: "Bild neu generieren",
  },
  regenerating: {
    en: "Regenerating...",
    tr: "Yenileniyor...",
    de: "Wird neu generiert...",
  },
  
  // My Stories
  mySharedStories: {
    en: "My Shared Stories",
    tr: "Paylaşılan Hikayelerim",
    de: "Meine geteilten Geschichten",
  },
  manageStories: {
    en: "Manage your shared stories and copy links to share with friends and family",
    tr: "Paylaşılan hikayelerinizi yönetin ve arkadaşlarınız ve ailenizle paylaşmak için bağlantıları kopyalayın",
    de: "Verwalte deine geteilten Geschichten und kopiere Links, um sie mit Freunden und Familie zu teilen",
  },
  noStoriesYet: {
    en: "No shared stories yet",
    tr: "Henüz paylaşılan hikaye yok",
    de: "Noch keine geteilten Geschichten",
  },
  createFirstStory: {
    en: "Create a story and share it to see it here!",
    tr: "Bir hikaye oluşturun ve burada görmek için paylaşın!",
    de: "Erstelle eine Geschichte und teile sie, um sie hier zu sehen!",
  },
  createYourFirstStory: {
    en: "Create Your First Story",
    tr: "İlk Hikayenizi Oluşturun",
    de: "Erstelle deine erste Geschichte",
  },
  panels: {
    en: "panels",
    tr: "panel",
    de: "Seiten",
  },
  copyLink: {
    en: "Copy Link",
    tr: "Linki Kopyala",
    de: "Link kopieren",
  },
  copied: {
    en: "Copied!",
    tr: "Kopyalandı!",
    de: "Kopiert!",
  },
  deleteStory: {
    en: "Delete this story?",
    tr: "Bu hikayeyi sil?",
    de: "Diese Geschichte löschen?",
  },
  deleteWarning: {
    en: "This will permanently delete this story and its share link will stop working. This action cannot be undone.",
    tr: "Bu, hikayeyi kalıcı olarak silecek ve paylaşım bağlantısı çalışmayı durduracaktır. Bu işlem geri alınamaz.",
    de: "Dies wird die Geschichte dauerhaft löschen und der Freigabelink funktioniert nicht mehr. Diese Aktion kann nicht rückgängig gemacht werden.",
  },
  cancel: {
    en: "Cancel",
    tr: "İptal",
    de: "Abbrechen",
  },
  delete: {
    en: "Delete",
    tr: "Sil",
    de: "Löschen",
  },
  backToCreate: {
    en: "Back to Create",
    tr: "Oluşturmaya Dön",
    de: "Zurück zum Erstellen",
  },
  
  // Shared Story
  storyNotFound: {
    en: "Story Not Found",
    tr: "Hikaye Bulunamadı",
    de: "Geschichte nicht gefunden",
  },
  storyNotFoundDesc: {
    en: "This story may have been removed or the link is invalid.",
    tr: "Bu hikaye kaldırılmış olabilir veya bağlantı geçersiz.",
    de: "Diese Geschichte wurde möglicherweise entfernt oder der Link ist ungültig.",
  },
  createYourOwn: {
    en: "Create Your Own Story",
    tr: "Kendi Hikayenizi Oluşturun",
    de: "Erstelle deine eigene Geschichte",
  },
  loadingStory: {
    en: "Loading story...",
    tr: "Hikaye yükleniyor...",
    de: "Geschichte wird geladen...",
  },
  
  // Footer
  madeWithLove: {
    en: "Made with 💖 for little readers everywhere",
    tr: "Her yerdeki küçük okurlar için 💖 ile yapıldı",
    de: "Mit 💖 für kleine Leser überall gemacht",
  },
  
  // Auth
  welcomeBack: {
    en: "Welcome back!",
    tr: "Tekrar hoş geldiniz!",
    de: "Willkommen zurück!",
  },
  signInToContinue: {
    en: "Sign in to continue creating magical stories",
    tr: "Sihirli hikayeler oluşturmaya devam etmek için giriş yapın",
    de: "Melde dich an, um weiter magische Geschichten zu erstellen",
  },
  createAccount: {
    en: "Create an account",
    tr: "Hesap oluştur",
    de: "Konto erstellen",
  },
  joinCommunity: {
    en: "Join our community of storytellers",
    tr: "Hikaye anlatıcıları topluluğumuza katılın",
    de: "Tritt unserer Gemeinschaft von Geschichtenerzählern bei",
  },
  email: {
    en: "Email",
    tr: "E-posta",
    de: "E-Mail",
  },
  password: {
    en: "Password",
    tr: "Şifre",
    de: "Passwort",
  },
  signingIn: {
    en: "Signing in...",
    tr: "Giriş yapılıyor...",
    de: "Anmelden...",
  },
  creatingAccount: {
    en: "Creating account...",
    tr: "Hesap oluşturuluyor...",
    de: "Konto wird erstellt...",
  },
  dontHaveAccount: {
    en: "Don't have an account?",
    tr: "Hesabınız yok mu?",
    de: "Kein Konto?",
  },
  signUp: {
    en: "Sign up",
    tr: "Kayıt ol",
    de: "Registrieren",
  },
  alreadyHaveAccount: {
    en: "Already have an account?",
    tr: "Zaten hesabınız var mı?",
    de: "Bereits ein Konto?",
  },
  forgotPassword: {
    en: "Forgot your password?",
    tr: "Şifrenizi mi unuttunuz?",
    de: "Passwort vergessen?",
  },
  forgotPasswordTitle: {
    en: "Reset Password",
    tr: "Şifre Sıfırla",
    de: "Passwort zurücksetzen",
  },
  forgotPasswordDesc: {
    en: "Enter your email and we'll send you a reset link",
    tr: "E-postanızı girin, size bir sıfırlama bağlantısı gönderelim",
    de: "Gib deine E-Mail ein und wir senden dir einen Link zum Zurücksetzen",
  },
  sendResetLink: {
    en: "Send Reset Link",
    tr: "Sıfırlama Linki Gönder",
    de: "Link senden",
  },
  resetEmailSent: {
    en: "Reset email sent!",
    tr: "Sıfırlama e-postası gönderildi!",
    de: "Reset-E-Mail gesendet!",
  },
  checkEmailForReset: {
    en: "Check your email for the password reset link",
    tr: "Şifre sıfırlama bağlantısı için e-postanızı kontrol edin",
    de: "Prüfe deine E-Mail für den Link zum Zurücksetzen des Passworts",
  },
  backToLogin: {
    en: "Back to login",
    tr: "Girişe dön",
    de: "Zurück zur Anmeldung",
  },
  setNewPasswordTitle: {
    en: "Set New Password",
    tr: "Yeni Şifre Belirle",
    de: "Neues Passwort festlegen",
  },
  setNewPasswordDesc: {
    en: "Enter your new password below",
    tr: "Aşağıya yeni şifrenizi girin",
    de: "Gib unten dein neues Passwort ein",
  },
  newPassword: {
    en: "New Password",
    tr: "Yeni Şifre",
    de: "Neues Passwort",
  },
  confirmNewPassword: {
    en: "Confirm New Password",
    tr: "Yeni Şifreyi Onayla",
    de: "Neues Passwort bestätigen",
  },
  updatePassword: {
    en: "Update Password",
    tr: "Şifreyi Güncelle",
    de: "Passwort aktualisieren",
  },
  passwordsDoNotMatch: {
    en: "Passwords do not match",
    tr: "Şifreler eşleşmiyor",
    de: "Passwörter stimmen nicht überein",
  },
  passwordUpdated: {
    en: "Password updated!",
    tr: "Şifre güncellendi!",
    de: "Passwort aktualisiert!",
  },
  passwordUpdatedDesc: {
    en: "You can now sign in with your new password",
    tr: "Artık yeni şifrenizle giriş yapabilirsiniz",
    de: "Du kannst dich jetzt mit deinem neuen Passwort anmelden",
  },
  confirmEmailSent: {
    en: "Confirmation email sent!",
    tr: "Onay e-postası gönderildi!",
    de: "Bestätigungs-E-Mail gesendet!",
  },
  checkEmailToConfirm: {
    en: "Please check your email to confirm your account before signing in",
    tr: "Giriş yapmadan önce lütfen hesabınızı onaylamak için e-postanızı kontrol edin",
    de: "Bitte überprüfe deine E-Mail, um dein Konto zu bestätigen, bevor du dich anmeldest",
  },
  
  // Toasts
  signedOutSuccess: {
    en: "Signed out successfully",
    tr: "Başarıyla çıkış yapıldı",
    de: "Erfolgreich abgemeldet",
  },
  storyReady: {
    en: "Your magical story is ready! ✨",
    tr: "Sihirli hikayeniz hazır! ✨",
    de: "Deine magische Geschichte ist fertig! ✨",
  },
  scrollToRead: {
    en: "Scroll down to read your adventure!",
    tr: "Maceranızı okumak için aşağı kaydırın!",
    de: "Scrolle nach unten, um dein Abenteuer zu lesen!",
  },
  somethingWentWrong: {
    en: "Oops! Something went wrong",
    tr: "Hata! Bir şeyler yanlış gitti",
    de: "Ups! Etwas ist schief gelaufen",
  },
  pleaseSignInToShare: {
    en: "Please sign in to share stories",
    tr: "Hikayeleri paylaşmak için lütfen giriş yapın",
    de: "Bitte melde dich an, um Geschichten zu teilen",
  },
  shareLinkCreated: {
    en: "Share link created!",
    tr: "Paylaşım linki oluşturuldu!",
    de: "Freigabelink erstellt!",
  },
  failedToCreateShareLink: {
    en: "Failed to create share link. Please try again.",
    tr: "Paylaşım linki oluşturulamadı. Lütfen tekrar deneyin.",
    de: "Freigabelink konnte nicht erstellt werden. Bitte versuche es erneut.",
  },
  linkCopied: {
    en: "Link copied to clipboard!",
    tr: "Link panoya kopyalandı!",
    de: "Link in die Zwischenablage kopiert!",
  },
  failedToCopy: {
    en: "Failed to copy link",
    tr: "Link kopyalanamadı",
    de: "Link konnte nicht kopiert werden",
  },
  imageRegenerated: {
    en: "Image regenerated!",
    tr: "Resim yenilendi!",
    de: "Bild neu generiert!",
  },
  failedToRegenerate: {
    en: "Failed to regenerate image. Please try again.",
    tr: "Resim yenilenemedi. Lütfen tekrar deneyin.",
    de: "Bild konnte nicht neu generiert werden. Bitte versuche es erneut.",
  },
  generatingPdf: {
    en: "Generating PDF...",
    tr: "PDF oluşturuluyor...",
    de: "PDF wird erstellt...",
  },
  pdfDownloaded: {
    en: "PDF downloaded!",
    tr: "PDF indirildi!",
    de: "PDF heruntergeladen!",
  },
  failedToGeneratePdf: {
    en: "Failed to generate PDF. Please try again.",
    tr: "PDF oluşturulamadı. Lütfen tekrar deneyin.",
    de: "PDF konnte nicht erstellt werden. Bitte versuche es erneut.",
  },
  storyDeleted: {
    en: "Story deleted",
    tr: "Hikaye silindi",
    de: "Geschichte gelöscht",
  },
  failedToDeleteStory: {
    en: "Failed to delete story",
    tr: "Hikaye silinemedi",
    de: "Geschichte konnte nicht gelöscht werden",
  },
  failedToLoadStories: {
    en: "Failed to load your stories",
    tr: "Hikayeleriniz yüklenemedi",
    de: "Deine Geschichten konnten nicht geladen werden",
  },
  loadingStories: {
    en: "Loading your stories...",
    tr: "Hikayeleriniz yükleniyor...",
    de: "Deine Geschichten werden geladen...",
  },
  creatingIllustration: {
    en: "Creating new illustration...",
    tr: "Yeni illüstrasyon oluşturuluyor...",
    de: "Neue Illustration wird erstellt...",
  },
  startingAdventure: {
    en: "Starting your magical adventure...",
    tr: "Sihirli maceranız başlıyor...",
    de: "Dein magisches Abenteuer beginnt...",
  },
  craftingStory: {
    en: "Crafting your story...",
    tr: "Hikayeniz hazırlanıyor...",
    de: "Deine Geschichte wird erstellt...",
  },
  creatingCharacterReferences: {
    en: "Creating character reference sheets...",
    tr: "Karakter referans çizimleri oluşturuluyor...",
    de: "Charakterreferenzblätter werden erstellt...",
  },
  creatingCharacterReference: {
    en: "Creating reference for {name}...",
    tr: "{name} için referans oluşturuluyor...",
    de: "Referenz für {name} wird erstellt...",
  },
  creatingCoverIllustration: {
    en: "Creating cover illustration...",
    tr: "Kapak illüstrasyonu oluşturuluyor...",
    de: "Titelbild wird erstellt...",
  },
  creatingIllustrationOf: {
    en: "Creating illustration {current} of {total}...",
    tr: "{total} illüstrasyondan {current}. oluşturuluyor...",
    de: "Illustration {current} von {total} wird erstellt...",
  },
  storyComplete: {
    en: "Story complete!",
    tr: "Hikaye tamamlandı!",
    de: "Geschichte fertig!",
  },
  
  // Story Queue
  yourStories: {
    en: "Your Stories",
    tr: "Hikayeleriniz",
    de: "Deine Geschichten",
  },
  queued: {
    en: "Queued",
    tr: "Sırada",
    de: "In Warteschlange",
  },
  generating: {
    en: "Generating",
    tr: "Oluşturuluyor",
    de: "Wird erstellt",
  },
  completed: {
    en: "Completed",
    tr: "Tamamlandı",
    de: "Fertig",
  },
  inProgress: {
    en: "in progress",
    tr: "devam ediyor",
    de: "in Bearbeitung",
  },
  ready: {
    en: "ready",
    tr: "hazır",
    de: "bereit",
  },
  waiting: {
    en: "waiting",
    tr: "bekliyor",
    de: "wartet",
  },
  viewStory: {
    en: "View Story",
    tr: "Hikayeyi Gör",
    de: "Geschichte ansehen",
  },
  backToStories: {
    en: "Back to Stories",
    tr: "Hikayelere Dön",
    de: "Zurück zu Geschichten",
  },
  continueInBackground: {
    en: "Continue in Background",
    tr: "Arka Planda Devam Et",
    de: "Im Hintergrund fortsetzen",
  },
  storiesGenerating: {
    en: "story generating",
    tr: "hikaye oluşturuluyor",
    de: "Geschichte wird erstellt",
  },
  storiesReady: {
    en: "story ready",
    tr: "hikaye hazır",
    de: "Geschichte bereit",
  },
  view: {
    en: "View",
    tr: "Görüntüle",
    de: "Ansehen",
  },
  cancelGeneration: {
    en: "Cancel Generation?",
    tr: "Üretimi İptal Et?",
    de: "Erstellung abbrechen?",
  },
  cancelGenerationDesc: {
    en: "Are you sure you want to cancel this story? The generation will stop and cannot be resumed.",
    tr: "Bu hikayeyi iptal etmek istediğinizden emin misiniz? Üretim duracak ve devam ettirilemez.",
    de: "Sind Sie sicher, dass Sie diese Geschichte abbrechen möchten? Die Erstellung wird gestoppt und kann nicht fortgesetzt werden.",
  },
  keepGenerating: {
    en: "Keep Generating",
    tr: "Üretmeye Devam Et",
    de: "Weiter erstellen",
  },
  cancelStory: {
    en: "Cancel Story",
    tr: "Hikayeyi İptal Et",
    de: "Geschichte abbrechen",
  },
  aStorySpark: {
    en: "A StorySpark Adventure",
    tr: "Bir StorySpark Macerası",
    de: "Ein StorySpark-Abenteuer",
  },
  theEnd: {
    en: "The End",
    tr: "Son",
    de: "Ende",
  },
  coverPage: {
    en: "Cover",
    tr: "Kapak",
    de: "Titelseite",
  },
  
  // StoryBuilder specific
  chooseYourCharacters: {
    en: "Choose Your Characters",
    tr: "Karakterlerini Seç",
    de: "Wähle deine Charaktere",
  },
  selectPresetOrCreate: {
    en: "Select preset characters or create your own. Assign main and side roles!",
    tr: "Hazır karakterleri seç veya kendininkini oluştur. Ana ve yan roller ata!",
    de: "Wähle vorgefertigte Charaktere oder erstelle deine eigenen. Weise Haupt- und Nebenrollen zu!",
  },
  main: {
    en: "Main:",
    tr: "Ana:",
    de: "Haupt:",
  },
  side: {
    en: "Side:",
    tr: "Yan:",
    de: "Neben:",
  },
  noneSelected: {
    en: "None selected",
    tr: "Hiçbiri seçilmedi",
    de: "Keine ausgewählt",
  },
  none: {
    en: "None",
    tr: "Yok",
    de: "Keine",
  },
  createNew: {
    en: "Create New",
    tr: "Yeni Oluştur",
    de: "Neu erstellen",
  },
  selectMainCharacter: {
    en: "Please select at least one main character",
    tr: "Lütfen en az bir ana karakter seçin",
    de: "Bitte wähle mindestens einen Hauptcharakter",
  },
  pickYourWorld: {
    en: "Pick Your World",
    tr: "Dünyasını Seç",
    de: "Wähle deine Welt",
  },
  whereWillStory: {
    en: "Where will your story take place? Select or create your own!",
    tr: "Hikayeniz nerede geçecek? Seç veya kendininkini oluştur!",
    de: "Wo wird deine Geschichte stattfinden? Wähle oder erstelle deine eigene!",
  },
  createWorld: {
    en: "Create World",
    tr: "Dünya Oluştur",
    de: "Welt erstellen",
  },
  whosReading: {
    en: "Who's Reading?",
    tr: "Kim Okuyor?",
    de: "Wer liest?",
  },
  adjustForReading: {
    en: "We'll adjust the story for the perfect reading level",
    tr: "Hikayeyi mükemmel okuma seviyesine göre ayarlayacağız",
    de: "Wir passen die Geschichte an das perfekte Leseniveau an",
  },
  whatsTheMessage: {
    en: "What's the Message?",
    tr: "Mesaj Ne?",
    de: "Was ist die Botschaft?",
  },
  chooseOrCreateLesson: {
    en: "Choose or create a beautiful lesson for your story",
    tr: "Hikayeniz için güzel bir ders seçin veya oluşturun",
    de: "Wähle oder erstelle eine schöne Lektion für deine Geschichte",
  },
  createTheme: {
    en: "Create Theme",
    tr: "Tema Oluştur",
    de: "Thema erstellen",
  },
  storyLength: {
    en: "Story Length",
    tr: "Hikaye Uzunluğu",
    de: "Geschichtenlänge",
  },
  storyLengthDesc: {
    en: "How long should your adventure be?",
    tr: "Maceranız ne kadar uzun olsun?",
    de: "Wie lang soll dein Abenteuer sein?",
  },
  storyLengthShort: {
    en: "Short",
    tr: "Kısa",
    de: "Kurz",
  },
  storyLengthShortDesc: {
    en: "3 pages - Quick read",
    tr: "3 sayfa - Hızlı okuma",
    de: "3 Seiten - Schnelles Lesen",
  },
  storyLengthMedium: {
    en: "Medium",
    tr: "Orta",
    de: "Mittel",
  },
  storyLengthMediumDesc: {
    en: "5 pages - Classic tale",
    tr: "5 sayfa - Klasik hikaye",
    de: "5 Seiten - Klassische Geschichte",
  },
  storyLengthLong: {
    en: "Long",
    tr: "Uzun",
    de: "Lang",
  },
  storyLengthLongDesc: {
    en: "8 pages - Epic adventure",
    tr: "8 sayfa - Destansı macera",
    de: "8 Seiten - Episches Abenteuer",
  },
  addYourOwnLesson: {
    en: "Add your own lesson",
    tr: "Kendi dersini ekle",
    de: "Füge deine eigene Lektion hinzu",
  },
  anySpecialRequests: {
    en: "Any Special Requests?",
    tr: "Özel İstekler Var mı?",
    de: "Besondere Wünsche?",
  },
  optionalDetails: {
    en: "Optional: Add specific details you'd like in the story",
    tr: "İsteğe bağlı: Hikayede olmasını istediğiniz özel detayları ekleyin",
    de: "Optional: Füge spezifische Details hinzu, die du in der Geschichte haben möchtest",
  },
  customPromptPlaceholder: {
    en: "E.g., 'Include a magical rainbow bridge' or 'Make the dragon sneeze sparkles'",
    tr: "Örn., 'Sihirli bir gökkuşağı köprüsü ekle' veya 'Ejderhanın pırıltılar hapşırmasını sağla'",
    de: "Z.B. 'Füge eine magische Regenbogenbrücke ein' oder 'Lass den Drachen Glitzer niesen'",
  },
  createMyStory: {
    en: "Create My Story!",
    tr: "Hikayemi Oluştur!",
    de: "Meine Geschichte erstellen!",
  },
  creatingYourStory: {
    en: "Creating Your Story...",
    tr: "Hikayeniz Oluşturuluyor...",
    de: "Deine Geschichte wird erstellt...",
  },
  progress: {
    en: "Progress",
    tr: "İlerleme",
    de: "Fortschritt",
  },
  selectCharacterToContinue: {
    en: "Select at least 1 character to continue",
    tr: "Devam etmek için en az 1 karakter seçin",
    de: "Wähle mindestens 1 Charakter, um fortzufahren",
  },
  assignMainCharacter: {
    en: "Assign at least 1 main character",
    tr: "En az 1 ana karakter atayın",
    de: "Weise mindestens 1 Hauptcharakter zu",
  },
  completeSelections: {
    en: "Complete all selections to continue",
    tr: "Devam etmek için tüm seçimleri tamamlayın",
    de: "Vervollständige alle Auswahlen, um fortzufahren",
  },
  characterSaved: {
    en: "Character saved!",
    tr: "Karakter kaydedildi!",
    de: "Charakter gespeichert!",
  },
  failedToSaveCharacter: {
    en: "Failed to save character",
    tr: "Karakter kaydedilemedi",
    de: "Charakter konnte nicht gespeichert werden",
  },
  characterUpdated: {
    en: "Character updated!",
    tr: "Karakter güncellendi!",
    de: "Charakter aktualisiert!",
  },
  failedToUpdateCharacter: {
    en: "Failed to update character",
    tr: "Karakter güncellenemedi",
    de: "Charakter konnte nicht aktualisiert werden",
  },
  characterDeleted: {
    en: "Character deleted",
    tr: "Karakter silindi",
    de: "Charakter gelöscht",
  },
  failedToDeleteCharacter: {
    en: "Failed to delete character",
    tr: "Karakter silinemedi",
    de: "Charakter konnte nicht gelöscht werden",
  },
  worldSaved: {
    en: "World saved!",
    tr: "Dünya kaydedildi!",
    de: "Welt gespeichert!",
  },
  failedToSaveWorld: {
    en: "Failed to save world",
    tr: "Dünya kaydedilemedi",
    de: "Welt konnte nicht gespeichert werden",
  },
  worldUpdated: {
    en: "World updated!",
    tr: "Dünya güncellendi!",
    de: "Welt aktualisiert!",
  },
  failedToUpdateWorld: {
    en: "Failed to update world",
    tr: "Dünya güncellenemedi",
    de: "Welt konnte nicht aktualisiert werden",
  },
  worldDeleted: {
    en: "World deleted",
    tr: "Dünya silindi",
    de: "Welt gelöscht",
  },
  failedToDeleteWorld: {
    en: "Failed to delete world",
    tr: "Dünya silinemedi",
    de: "Welt konnte nicht gelöscht werden",
  },
  themeSaved: {
    en: "Theme saved!",
    tr: "Tema kaydedildi!",
    de: "Thema gespeichert!",
  },
  failedToSaveTheme: {
    en: "Failed to save theme",
    tr: "Tema kaydedilemedi",
    de: "Thema konnte nicht gespeichert werden",
  },
  themeUpdated: {
    en: "Theme updated!",
    tr: "Tema güncellendi!",
    de: "Thema aktualisiert!",
  },
  failedToUpdateTheme: {
    en: "Failed to update theme",
    tr: "Tema güncellenemedi",
    de: "Thema konnte nicht aktualisiert werden",
  },
  themeDeleted: {
    en: "Theme deleted",
    tr: "Tema silindi",
    de: "Thema gelöscht",
  },
  failedToDeleteTheme: {
    en: "Failed to delete theme",
    tr: "Tema silinemedi",
    de: "Thema konnte nicht gelöscht werden",
  },
  
  // Visuals & Music Options
  visualsAndMusic: {
    en: "Visuals & Music",
    tr: "Görseller ve Müzik",
    de: "Bilder & Musik",
  },
  visualsAndMusicDesc: {
    en: "Customize your story experience with illustrations and background music",
    tr: "Hikaye deneyiminizi illüstrasyonlar ve arka plan müziği ile özelleştirin",
    de: "Passe dein Geschichte-Erlebnis mit Illustrationen und Hintergrundmusik an",
  },
  includeVisuals: {
    en: "Include Illustrations",
    tr: "İllüstrasyonlar Ekle",
    de: "Illustrationen einschließen",
  },
  includeVisualsDesc: {
    en: "Generate beautiful AI illustrations for each page",
    tr: "Her sayfa için güzel yapay zeka illüstrasyonları oluştur",
    de: "Erzeuge schöne KI-Illustrationen für jede Seite",
  },
  textOnlyStory: {
    en: "Text Only",
    tr: "Sadece Metin",
    de: "Nur Text",
  },
  textOnlyStoryDesc: {
    en: "Story without illustrations (faster)",
    tr: "İllüstrasyonsuz hikaye (daha hızlı)",
    de: "Geschichte ohne Illustrationen (schneller)",
  },
  selectBackgroundMusic: {
    en: "Background Music",
    tr: "Arka Plan Müziği",
    de: "Hintergrundmusik",
  },
  musicMagical: {
    en: "Magical",
    tr: "Sihirli",
    de: "Magisch",
  },
  musicMagicalDesc: {
    en: "Whimsical, enchanting melodies",
    tr: "Kaprisli, büyüleyici melodiler",
    de: "Verspielte, bezaubernde Melodien",
  },
  musicAdventure: {
    en: "Adventure",
    tr: "Macera",
    de: "Abenteuer",
  },
  musicAdventureDesc: {
    en: "Exciting, upbeat journey sounds",
    tr: "Heyecanlı, neşeli yolculuk sesleri",
    de: "Aufregende, beschwingte Reiseklänge",
  },
  musicCalm: {
    en: "Calm",
    tr: "Sakin",
    de: "Ruhig",
  },
  musicCalmDesc: {
    en: "Peaceful, soothing background",
    tr: "Huzurlu, yatıştırıcı arka plan",
    de: "Friedlicher, beruhigender Hintergrund",
  },
  musicNone: {
    en: "No Music",
    tr: "Müzik Yok",
    de: "Keine Musik",
  },
  musicNoneDesc: {
    en: "Story without background music",
    tr: "Arka plan müziği olmadan hikaye",
    de: "Geschichte ohne Hintergrundmusik",
  },
  
  // Auth page
  storyCreator: {
    en: "StorySpark",
    tr: "StorySpark",
    de: "StorySpark",
  },
  createMagicalStories: {
    en: "Create magical stories for children",
    tr: "Çocuklar için sihirli hikayeler oluşturun",
    de: "Erstelle magische Geschichten für Kinder",
  },
  welcomeBackTitle: {
    en: "Welcome Back!",
    tr: "Tekrar Hoş Geldiniz!",
    de: "Willkommen zurück!",
  },
  joinTheAdventure: {
    en: "Join the Adventure!",
    tr: "Maceraya Katıl!",
    de: "Werde Teil des Abenteuers!",
  },
  signInToAccess: {
    en: "Sign in to access your saved characters, worlds, and themes",
    tr: "Kayıtlı karakterlerinize, dünyalarınıza ve temalarınıza erişmek için giriş yapın",
    de: "Melde dich an, um auf deine gespeicherten Charaktere, Welten und Themen zuzugreifen",
  },
  createAccountToSave: {
    en: "Create an account to save your custom creations",
    tr: "Özel yaratımlarınızı kaydetmek için bir hesap oluşturun",
    de: "Erstelle ein Konto, um deine benutzerdefinierten Kreationen zu speichern",
  },
  pleaseWait: {
    en: "Please wait...",
    tr: "Lütfen bekleyin...",
    de: "Bitte warten...",
  },
  createAccountBtn: {
    en: "Create Account",
    tr: "Hesap Oluştur",
    de: "Konto erstellen",
  },
  dontHaveAccountSignUp: {
    en: "Don't have an account? Sign up",
    tr: "Hesabınız yok mu? Kayıt olun",
    de: "Kein Konto? Registrieren",
  },
  alreadyHaveAccountSignIn: {
    en: "Already have an account? Sign in",
    tr: "Zaten hesabınız var mı? Giriş yapın",
    de: "Bereits ein Konto? Anmelden",
  },
  validationError: {
    en: "Validation Error",
    tr: "Doğrulama Hatası",
    de: "Validierungsfehler",
  },
  loginFailed: {
    en: "Login Failed",
    tr: "Giriş Başarısız",
    de: "Anmeldung fehlgeschlagen",
  },
  invalidCredentials: {
    en: "Invalid email or password. Please try again.",
    tr: "Geçersiz e-posta veya şifre. Lütfen tekrar deneyin.",
    de: "Ungültige E-Mail oder Passwort. Bitte versuche es erneut.",
  },
  welcomeBackToast: {
    en: "You have successfully logged in.",
    tr: "Başarıyla giriş yaptınız.",
    de: "Du hast dich erfolgreich angemeldet.",
  },
  signupFailed: {
    en: "Signup Failed",
    tr: "Kayıt Başarısız",
    de: "Registrierung fehlgeschlagen",
  },
  emailAlreadyRegistered: {
    en: "This email is already registered. Please login instead.",
    tr: "Bu e-posta zaten kayıtlı. Lütfen giriş yapın.",
    de: "Diese E-Mail ist bereits registriert. Bitte melde dich stattdessen an.",
  },
  accountCreated: {
    en: "Account Created!",
    tr: "Hesap Oluşturuldu!",
    de: "Konto erstellt!",
  },
  welcomeToStoryCreator: {
    en: "Welcome to Story Creator! You can now create your own stories.",
    tr: "Hikaye Oluşturucu'ya hoş geldiniz! Artık kendi hikayelerinizi oluşturabilirsiniz.",
    de: "Willkommen beim Geschichtenersteller! Du kannst jetzt deine eigenen Geschichten erstellen.",
  },
  unexpectedError: {
    en: "An unexpected error occurred. Please try again.",
    tr: "Beklenmeyen bir hata oluştu. Lütfen tekrar deneyin.",
    de: "Ein unerwarteter Fehler ist aufgetreten. Bitte versuche es erneut.",
  },
  error: {
    en: "Error",
    tr: "Hata",
    de: "Fehler",
  },
  
  // Age Groups
  ageToddlerLabel: {
    en: "Little Ones",
    tr: "Minikler",
    de: "Die Kleinen",
  },
  ageToddlerDesc: {
    en: "Simple words, big pictures",
    tr: "Basit kelimeler, büyük resimler",
    de: "Einfache Wörter, große Bilder",
  },
  ageToddlerRange: {
    en: "2-4 years",
    tr: "2-4 yaş",
    de: "2-4 Jahre",
  },
  agePreschoolLabel: {
    en: "Pre-Schoolers",
    tr: "Okul Öncesi",
    de: "Vorschulkinder",
  },
  agePreschoolDesc: {
    en: "Short sentences, fun adventures",
    tr: "Kısa cümleler, eğlenceli maceralar",
    de: "Kurze Sätze, lustige Abenteuer",
  },
  agePreschoolRange: {
    en: "4-6 years",
    tr: "4-6 yaş",
    de: "4-6 Jahre",
  },
  ageEarlyReaderLabel: {
    en: "Early Readers",
    tr: "Yeni Okuyucular",
    de: "Leseanfänger",
  },
  ageEarlyReaderDesc: {
    en: "More story, exciting plots",
    tr: "Daha fazla hikaye, heyecanlı olaylar",
    de: "Mehr Geschichte, spannende Handlungen",
  },
  ageEarlyReaderRange: {
    en: "6-8 years",
    tr: "6-8 yaş",
    de: "6-8 Jahre",
  },
  ageChapterReadyLabel: {
    en: "Chapter Ready",
    tr: "Bölüm Okuyucuları",
    de: "Kapitelleser",
  },
  ageChapterReadyDesc: {
    en: "Longer tales, deeper lessons",
    tr: "Uzun hikayeler, derin dersler",
    de: "Längere Geschichten, tiefere Lektionen",
  },
  ageChapterReadyRange: {
    en: "8-10 years",
    tr: "8-10 yaş",
    de: "8-10 Jahre",
  },
  
  // Morals/Themes
  moralKindnessLabel: {
    en: "Be Kind",
    tr: "Nazik Ol",
    de: "Sei freundlich",
  },
  moralKindnessDesc: {
    en: "Treating others with care and compassion",
    tr: "Başkalarına özen ve şefkatle davranmak",
    de: "Andere mit Fürsorge und Mitgefühl behandeln",
  },
  moralBraveryLabel: {
    en: "Be Brave",
    tr: "Cesur Ol",
    de: "Sei mutig",
  },
  moralBraveryDesc: {
    en: "Facing fears and trying new things",
    tr: "Korkularla yüzleşmek ve yeni şeyler denemek",
    de: "Ängste überwinden und Neues ausprobieren",
  },
  moralFriendshipLabel: {
    en: "Friendship",
    tr: "Arkadaşlık",
    de: "Freundschaft",
  },
  moralFriendshipDesc: {
    en: "The magic of having good friends",
    tr: "İyi arkadaşlara sahip olmanın büyüsü",
    de: "Die Magie guter Freundschaften",
  },
  moralHonestyLabel: {
    en: "Be Honest",
    tr: "Dürüst Ol",
    de: "Sei ehrlich",
  },
  moralHonestyDesc: {
    en: "The importance of telling the truth",
    tr: "Doğruyu söylemenin önemi",
    de: "Die Wichtigkeit der Wahrheit",
  },
  moralSharingLabel: {
    en: "Sharing is Caring",
    tr: "Paylaşmak Güzeldir",
    de: "Teilen ist Fürsorge",
  },
  moralSharingDesc: {
    en: "Joy of sharing with others",
    tr: "Başkalarıyla paylaşmanın sevinci",
    de: "Die Freude am Teilen mit anderen",
  },
  moralPerseveranceLabel: {
    en: "Never Give Up",
    tr: "Asla Vazgeçme",
    de: "Gib niemals auf",
  },
  moralPerseveranceDesc: {
    en: "Keep trying even when it's hard",
    tr: "Zor olsa bile denemeye devam et",
    de: "Weitermachen, auch wenn es schwer ist",
  },
  
  // Music
  backgroundMusic: {
    en: "Background Music",
    tr: "Arka Plan Müziği",
    de: "Hintergrundmusik",
  },
  musicThemeMagical: {
    en: "Magical",
    tr: "Sihirli",
    de: "Magisch",
  },
  musicThemeAdventure: {
    en: "Adventure",
    tr: "Macera",
    de: "Abenteuer",
  },
  musicThemeCalm: {
    en: "Calm",
    tr: "Sakin",
    de: "Ruhig",
  },
  generatingMusic: {
    en: "Generating music...",
    tr: "Müzik oluşturuluyor...",
    de: "Musik wird generiert...",
  },
  musicOff: {
    en: "Music Off",
    tr: "Müzik Kapalı",
    de: "Musik Aus",
  },
  
  // Read Aloud
  readAloud: {
    en: "Read Aloud",
    tr: "Sesli Oku",
    de: "Vorlesen",
  },
  stopReading: {
    en: "Stop",
    tr: "Dur",
    de: "Stopp",
  },
  loadingAudio: {
    en: "Loading...",
    tr: "Yükleniyor...",
    de: "Laden...",
  },
  voiceSelection: {
    en: "Voice",
    tr: "Ses",
    de: "Stimme",
  },
  voiceFemale: {
    en: "Female",
    tr: "Kadın",
    de: "Weiblich",
  },
  voiceMale: {
    en: "Male",
    tr: "Erkek",
    de: "Männlich",
  },
  voiceChild: {
    en: "Child",
    tr: "Çocuk",
    de: "Kind",
  },
  
  // Loading Entertainment
  creatingMagic: {
    en: "Creating Magic",
    tr: "Sihir Yaratılıyor",
    de: "Magie wird erschaffen",
  },
  yourStoryIsBrewing: {
    en: "Your Story is Brewing... ✨",
    tr: "Hikayeniz Hazırlanıyor... ✨",
    de: "Deine Geschichte entsteht... ✨",
  },
  starring: {
    en: "Starring",
    tr: "Başrollerde",
    de: "Mit",
  },
  with: {
    en: "With",
    tr: "İle",
    de: "Mit",
  },
  funFactMagic: {
    en: "Did you know? Every story has a little bit of magic! ✨",
    tr: "Biliyor muydunuz? Her hikayede biraz sihir vardır! ✨",
    de: "Wusstest du? Jede Geschichte hat ein bisschen Magie! ✨",
  },
  funFactStory: {
    en: "Stories help us dream bigger and reach for the stars! 🌟",
    tr: "Hikayeler daha büyük hayal kurmamıza yardımcı olur! 🌟",
    de: "Geschichten helfen uns, größer zu träumen! 🌟",
  },
  funFactCharacter: {
    en: "Your characters are getting ready for their big adventure! 🎭",
    tr: "Karakterleriniz büyük maceraları için hazırlanıyor! 🎭",
    de: "Deine Charaktere bereiten sich auf ihr großes Abenteuer vor! 🎭",
  },
  funFactAdventure: {
    en: "Every adventure starts with a single step... or page! 📖",
    tr: "Her macera tek bir adımla... veya sayfayla başlar! 📖",
    de: "Jedes Abenteuer beginnt mit einem einzigen Schritt... oder einer Seite! 📖",
  },
  funFactImagination: {
    en: "Imagination is the most powerful magic of all! 🎨",
    tr: "Hayal gücü en güçlü sihirdir! 🎨",
    de: "Fantasie ist die mächtigste Magie überhaupt! 🎨",
  },
  loadingMsgGathering: {
    en: "Gathering magical ingredients...",
    tr: "Sihirli malzemeler toplanıyor...",
    de: "Magische Zutaten werden gesammelt...",
  },
  loadingMsgSprinkle: {
    en: "Sprinkling some fairy dust...",
    tr: "Biraz peri tozu serpiliyor...",
    de: "Ein bisschen Feenstaub wird gestreut...",
  },
  loadingMsgWeaving: {
    en: "Weaving words into wonder...",
    tr: "Kelimeler harikaya dönüşüyor...",
    de: "Worte werden zu Wundern verwoben...",
  },
  loadingMsgPainting: {
    en: "Painting magical scenes...",
    tr: "Sihirli sahneler çiziliyor...",
    de: "Magische Szenen werden gemalt...",
  },
  loadingMsgAlmostReady: {
    en: "Almost ready... the magic is nearly complete! ✨",
    tr: "Neredeyse hazır... sihir neredeyse tamamlandı! ✨",
    de: "Fast fertig... die Magie ist fast vollständig! ✨",
  },
  
  // Interactive Branching
  yourChoice: {
    en: "Your Choice",
    tr: "Senin Seçimin",
    de: "Deine Wahl",
  },
  generatingBranch: {
    en: "Creating your adventure path...",
    tr: "Macera yolunuz oluşturuluyor...",
    de: "Dein Abenteuerpfad wird erstellt...",
  },
  yourPath: {
    en: "Your path",
    tr: "Senin yolun",
    de: "Dein Pfad",
  },
  startOver: {
    en: "Start Over",
    tr: "Baştan Başla",
    de: "Von vorne",
  },
  tryOtherPath: {
    en: "Try Another Path",
    tr: "Başka Yolu Dene",
    de: "Anderen Pfad versuchen",
  },
  interactiveChoices: {
    en: "Interactive Choices",
    tr: "Etkileşimli Seçimler",
    de: "Interaktive Entscheidungen",
  },
  interactiveChoicesDesc: {
    en: "Let readers make decisions that change the story",
    tr: "Okuyucuların hikayeyi değiştiren kararlar vermesine izin ver",
    de: "Lass Leser Entscheidungen treffen, die die Geschichte verändern",
  },
  enableBranching: {
    en: "Choose Your Own Adventure",
    tr: "Kendi Maceranı Seç",
    de: "Wähle dein eigenes Abenteuer",
  },
  enableBranchingDesc: {
    en: "Your child decides what happens next! At key moments, they'll choose between two paths — each leading to a different ending. Perfect for re-reading!",
    tr: "Çocuğunuz sırada ne olacağına karar verir! Önemli anlarda, iki yol arasında seçim yapacaklar — her biri farklı bir sona götürür. Tekrar okumak için mükemmel!",
    de: "Dein Kind entscheidet, was als nächstes passiert! An Schlüsselmomenten wählen sie zwischen zwei Wegen — jeder führt zu einem anderen Ende. Perfekt zum Wiederlesen!",
  },
  branchingExample: {
    en: "Example: \"Should Luna follow the glowing path or explore the dark cave?\"",
    tr: "Örnek: \"Luna parlayan yolu mu takip etmeli yoksa karanlık mağarayı mı keşfetmeli?\"",
    de: "Beispiel: \"Sollte Luna dem leuchtenden Pfad folgen oder die dunkle Höhle erkunden?\"",
  },
  branchGenerated: {
    en: "New adventure path created!",
    tr: "Yeni macera yolu oluşturuldu!",
    de: "Neuer Abenteuerpfad erstellt!",
  },
  branchGenerationFailed: {
    en: "Failed to create adventure path. Please try again.",
    tr: "Macera yolu oluşturulamadı. Lütfen tekrar deneyin.",
    de: "Abenteuerpfad konnte nicht erstellt werden. Bitte versuche es erneut.",
  },
  storyEnd: {
    en: "The End",
    tr: "Son",
    de: "Ende",
  },
  exploreAnotherPath: {
    en: "Explore Another Path",
    tr: "Başka Bir Yolu Keşfet",
    de: "Einen anderen Pfad erkunden",
  },
  branchingActiveTitle: {
    en: "Interactive Story Mode Active!",
    tr: "Etkileşimli Hikaye Modu Aktif!",
    de: "Interaktiver Geschichtenmodus aktiv!",
  },
  branchingHint1: {
    en: "Decision points will appear during the story",
    tr: "Hikaye sırasında karar noktaları görünecek",
    de: "Entscheidungspunkte werden während der Geschichte erscheinen",
  },
  branchingHint2: {
    en: "Choose different paths to explore unique endings",
    tr: "Benzersiz sonları keşfetmek için farklı yollar seçin",
    de: "Wähle verschiedene Wege, um einzigartige Enden zu erkunden",
  },
  branchingHint3: {
    en: "Try different choices to see all story branches",
    tr: "Tüm hikaye dallarını görmek için farklı seçenekleri deneyin",
    de: "Probiere verschiedene Entscheidungen aus, um alle Handlungsstränge zu sehen",
  },
  close: {
    en: "Close",
    tr: "Kapat",
    de: "Schließen",
  },
  speed: {
    en: "speed",
    tr: "hız",
    de: "Geschwindigkeit",
  },
  femaleVoice: {
    en: "Female voice",
    tr: "Kadın sesi",
    de: "Weibliche Stimme",
  },
  maleVoice: {
    en: "Male voice",
    tr: "Erkek sesi",
    de: "Männliche Stimme",
  },
  childVoice: {
    en: "Child voice",
    tr: "Çocuk sesi",
    de: "Kinderstimme",
  },
  interactiveStory: {
    en: "Interactive",
    tr: "Etkileşimli",
    de: "Interaktiv",
  },
  readAloudError: {
    en: "Failed to read aloud",
    tr: "Sesli okuma başarısız",
    de: "Vorlesen fehlgeschlagen",
  },
  readEntireStory: {
    en: "Read Entire Story",
    tr: "Tüm Hikayeyi Oku",
    de: "Ganze Geschichte vorlesen",
  },
  stopReadingAll: {
    en: "Stop Reading",
    tr: "Okumayı Durdur",
    de: "Vorlesen stoppen",
  },
  readingPage: {
    en: "Reading page {current} of {total}...",
    tr: "Sayfa {current} / {total} okunuyor...",
    de: "Seite {current} von {total} wird vorgelesen...",
  },
  finishedReading: {
    en: "Finished reading the story!",
    tr: "Hikaye okundu!",
    de: "Geschichte fertig vorgelesen!",
  },
  fullscreen: {
    en: "Fullscreen",
    tr: "Tam Ekran",
    de: "Vollbild",
  },
  exitFullscreen: {
    en: "Exit Fullscreen",
    tr: "Tam Ekrandan Çık",
    de: "Vollbild beenden",
  },
  storyPath: {
    en: "Story Path",
    tr: "Hikaye Yolu",
    de: "Handlungspfad",
  },
  currentPath: {
    en: "Current Path",
    tr: "Mevcut Yol",
    de: "Aktueller Pfad",
  },

  // Landing Page
  getStarted: {
    en: "Get Started",
    tr: "Başla",
    de: "Loslegen",
  },
  heroTagline: {
    en: "AI-Powered Story Magic",
    tr: "Yapay Zeka Destekli Hikaye Sihri",
    de: "KI-gestützte Geschichtsmagie",
  },
  heroTitle: {
    en: "Create Magical Stories",
    tr: "Sihirli Hikayeler Oluştur",
    de: "Erstelle Magische Geschichten",
  },
  heroSubtitle: {
    en: "For Your Little Ones",
    tr: "Çocuklar İçin",
    de: "Für Ihre Kleinen",
  },
  landingHeroDescription: {
    en: "Generate personalized, illustrated bedtime stories in seconds. Choose characters, worlds, and morals—let AI do the rest.",
    tr: "Saniyeler içinde kişiselleştirilmiş, resimli uyku hikayeleri oluşturun. Karakterler, dünyalar ve değerler seçin—gerisini yapay zekaya bırakın.",
    de: "Erstellen Sie personalisierte, illustrierte Gute-Nacht-Geschichten in Sekunden. Wählen Sie Charaktere, Welten und Moral—lassen Sie die KI den Rest erledigen.",
  },
  startCreatingFree: {
    en: "Start Creating Free",
    tr: "Ücretsiz Oluşturmaya Başla",
    de: "Kostenlos Erstellen",
  },
  learnMore: {
    en: "Learn More",
    tr: "Daha Fazla Bilgi",
    de: "Mehr Erfahren",
  },
  previewTitle: {
    en: "Your Story Awaits...",
    tr: "Hikayeniz Sizi Bekliyor...",
    de: "Ihre Geschichte Wartet...",
  },
  previewDescription: {
    en: "Sign up to create your first magical adventure",
    tr: "İlk sihirli maceranızı oluşturmak için kaydolun",
    de: "Registrieren Sie sich, um Ihr erstes magisches Abenteuer zu erstellen",
  },
  featuresTitle: {
    en: "Everything You Need to Create Magic",
    tr: "Sihir Yaratmak İçin İhtiyacınız Olan Her Şey",
    de: "Alles, Was Sie Brauchen, Um Magie Zu Erschaffen",
  },
  featuresDescription: {
    en: "Powerful features designed to make storytelling easy and fun for everyone.",
    tr: "Hikaye anlatımını herkes için kolay ve eğlenceli hale getirmek üzere tasarlanmış güçlü özellikler.",
    de: "Leistungsstarke Funktionen, die das Geschichtenerzählen für alle einfach und unterhaltsam machen.",
  },
  featureAiTitle: {
    en: "AI-Powered Stories",
    tr: "Yapay Zeka Destekli Hikayeler",
    de: "KI-gestützte Geschichten",
  },
  featureAiDesc: {
    en: "Create unique, personalized stories with advanced AI that understands your child's interests.",
    tr: "Çocuğunuzun ilgi alanlarını anlayan gelişmiş yapay zeka ile benzersiz, kişiselleştirilmiş hikayeler oluşturun.",
    de: "Erstellen Sie einzigartige, personalisierte Geschichten mit fortschrittlicher KI, die die Interessen Ihres Kindes versteht.",
  },
  featureCharactersTitle: {
    en: "Choose Characters",
    tr: "Karakter Seçin",
    de: "Charaktere Wählen",
  },
  featureCharactersDesc: {
    en: "Select from magical characters like dragons, unicorns, wizards, and more—or create your own!",
    tr: "Ejderhalar, tek boynuzlular, büyücüler gibi sihirli karakterlerden seçin—veya kendinizinkini oluşturun!",
    de: "Wählen Sie aus magischen Charakteren wie Drachen, Einhörnern, Zauberern und mehr—oder erstellen Sie Ihre eigenen!",
  },
  featureIllustrationsTitle: {
    en: "Beautiful Illustrations",
    tr: "Güzel İllüstrasyonlar",
    de: "Wunderschöne Illustrationen",
  },
  featureIllustrationsDesc: {
    en: "Every story comes with stunning AI-generated artwork that brings the adventure to life.",
    tr: "Her hikaye, maceraya hayat veren çarpıcı yapay zeka tarafından oluşturulmuş resimlerle birlikte gelir.",
    de: "Jede Geschichte enthält atemberaubende KI-generierte Kunstwerke, die das Abenteuer zum Leben erwecken.",
  },
  featureReadAloudTitle: {
    en: "Read Aloud",
    tr: "Sesli Oku",
    de: "Vorlesen",
  },
  featureReadAloudDesc: {
    en: "Listen to stories with natural voice narration—perfect for bedtime or learning to read.",
    tr: "Doğal ses anlatımıyla hikayeleri dinleyin—uyku vakti veya okuma öğrenimi için mükemmel.",
    de: "Hören Sie Geschichten mit natürlicher Sprachausgabe—perfekt zum Einschlafen oder Lesenlernen.",
  },
  whyChooseUs: {
    en: "Why Parents Love Us",
    tr: "Ebeveynler Neden Bizi Seviyor",
    de: "Warum Eltern Uns Lieben",
  },
  benefit1: {
    en: "Unlimited creative stories tailored to your child",
    tr: "Çocuğunuza özel sınırsız yaratıcı hikayeler",
    de: "Unbegrenzte kreative Geschichten für Ihr Kind",
  },
  benefit2: {
    en: "Choose from multiple worlds: castles, forests, space & more",
    tr: "Birden fazla dünya seçin: kaleler, ormanlar, uzay ve daha fazlası",
    de: "Wählen Sie aus mehreren Welten: Schlösser, Wälder, Weltraum und mehr",
  },
  benefit3: {
    en: "Interactive branching stories with choices",
    tr: "Seçenekli etkileşimli dallanmış hikayeler",
    de: "Interaktive verzweigte Geschichten mit Auswahlmöglichkeiten",
  },
  benefit4: {
    en: "Save and share favorite stories",
    tr: "Favori hikayeleri kaydedin ve paylaşın",
    de: "Speichern und teilen Sie Lieblingsgeschichten",
  },
  benefit5: {
    en: "Age-appropriate content for all kids",
    tr: "Tüm çocuklar için yaşa uygun içerik",
    de: "Altersgerechte Inhalte für alle Kinder",
  },
  benefit6: {
    en: "Background music for immersive experience",
    tr: "Sürükleyici deneyim için arka plan müziği",
    de: "Hintergrundmusik für ein immersives Erlebnis",
  },
  testimonialQuote: {
    en: "My kids absolutely love the stories! Every night they ask for a new adventure. It's become our favorite bedtime ritual.",
    tr: "Çocuklarım hikayeleri çok seviyor! Her gece yeni bir macera istiyorlar. En sevdiğimiz uyku vakti ritüelimiz oldu.",
    de: "Meine Kinder lieben die Geschichten! Jeden Abend fragen sie nach einem neuen Abenteuer. Es ist unser liebstes Einschlafritual geworden.",
  },
  testimonialAuthor: {
    en: "Sarah M., Parent of 2",
    tr: "Ayşe M., 2 Çocuk Annesi",
    de: "Sarah M., Mutter von 2 Kindern",
  },
  ctaTitle: {
    en: "Ready to Create Magic?",
    tr: "Sihir Yaratmaya Hazır Mısınız?",
    de: "Bereit, Magie Zu Erschaffen?",
  },
  ctaDescription: {
    en: "Join thousands of families creating unforgettable stories together.",
    tr: "Birlikte unutulmaz hikayeler oluşturan binlerce aileye katılın.",
    de: "Schließen Sie sich Tausenden von Familien an, die gemeinsam unvergessliche Geschichten erschaffen.",
  },
  signUpNow: {
    en: "Sign Up Now — It's Free",
    tr: "Şimdi Kaydolun — Ücretsiz",
    de: "Jetzt Registrieren — Kostenlos",
  },
  footerText: {
    en: "Made with ❤️ for little dreamers everywhere",
    tr: "Her yerdeki küçük hayalperestler için ❤️ ile yapıldı",
    de: "Mit ❤️ für kleine Träumer überall gemacht",
  },
  backToHome: {
    en: "Back",
    tr: "Geri",
    de: "Zurück",
  },
  seeTheMagic: {
    en: "See the Magic in Action",
    tr: "Sihri Aksiyonda Görün",
    de: "Sehen Sie die Magie in Aktion",
  },
  seeTheMagicDesc: {
    en: "Watch how easy it is to create a personalized story for your child in just seconds.",
    tr: "Çocuğunuz için saniyeler içinde kişiselleştirilmiş bir hikaye oluşturmanın ne kadar kolay olduğunu izleyin.",
    de: "Sehen Sie, wie einfach es ist, in nur wenigen Sekunden eine personalisierte Geschichte für Ihr Kind zu erstellen.",
  },
  watchTheDemo: {
    en: "Watch the Magic",
    tr: "Sihri İzleyin",
    de: "Die Magie ansehen",
  },
  tryItYourself: {
    en: "Try It Yourself",
    tr: "Kendiniz Deneyin",
    de: "Selbst Ausprobieren",
  },
  
  // FAQ Section
  faqTitle: {
    en: "Frequently Asked Questions",
    tr: "Sıkça Sorulan Sorular",
    de: "Häufig gestellte Fragen",
  },
  faqSubtitle: {
    en: "Everything you need to know about creating magical stories",
    tr: "Sihirli hikayeler oluşturmak hakkında bilmeniz gereken her şey",
    de: "Alles, was Sie über das Erstellen magischer Geschichten wissen müssen",
  },
  faq1Question: {
    en: "How does StorySpark create stories?",
    tr: "StorySpark hikayeleri nasıl oluşturur?",
    de: "Wie erstellt StorySpark Geschichten?",
  },
  faq1Answer: {
    en: "StorySpark uses advanced AI to generate unique, personalized stories based on your character selections, world choice, and chosen theme. Each story is illustrated with beautiful AI-generated artwork tailored to your story.",
    tr: "StorySpark, karakter seçimlerinize, dünya tercihinize ve seçtiğiniz temaya göre benzersiz, kişiselleştirilmiş hikayeler oluşturmak için gelişmiş yapay zeka kullanır. Her hikaye, hikayenize özel güzel yapay zeka tarafından oluşturulan çizimlerle süslenir.",
    de: "StorySpark verwendet fortschrittliche KI, um einzigartige, personalisierte Geschichten basierend auf Ihrer Charakterauswahl, Weltauswahl und Ihrem gewählten Thema zu erstellen. Jede Geschichte wird mit wunderschönen, KI-generierten Kunstwerken illustriert.",
  },
  faq2Question: {
    en: "What age groups are the stories suitable for?",
    tr: "Hikayeler hangi yaş grupları için uygundur?",
    de: "Für welche Altersgruppen sind die Geschichten geeignet?",
  },
  faq2Answer: {
    en: "Our stories are designed for children ages 2-10. You can select the specific age group, and the AI will adjust the vocabulary, story complexity, and themes to be age-appropriate and engaging.",
    tr: "Hikayelerimiz 2-10 yaş arası çocuklar için tasarlanmıştır. Belirli yaş grubunu seçebilirsiniz ve yapay zeka kelime dağarcığını, hikaye karmaşıklığını ve temaları yaşa uygun ve ilgi çekici olacak şekilde ayarlayacaktır.",
    de: "Unsere Geschichten sind für Kinder im Alter von 2-10 Jahren konzipiert. Sie können die spezifische Altersgruppe auswählen, und die KI passt Wortschatz, Geschichtenkomplexität und Themen altersgerecht und ansprechend an.",
  },
  faq3Question: {
    en: "Can I create my own characters and worlds?",
    tr: "Kendi karakterlerimi ve dünyalarımı oluşturabilir miyim?",
    de: "Kann ich meine eigenen Charaktere und Welten erstellen?",
  },
  faq3Answer: {
    en: "Yes! You can create custom characters with unique names, descriptions, and appearances. You can also create custom worlds and themes. Sign up for a free account to save your creations and use them in multiple stories.",
    tr: "Evet! Benzersiz isimler, açıklamalar ve görünümlerle özel karakterler oluşturabilirsiniz. Ayrıca özel dünyalar ve temalar da oluşturabilirsiniz. Yaratımlarınızı kaydetmek ve birden fazla hikayede kullanmak için ücretsiz bir hesap oluşturun.",
    de: "Ja! Sie können benutzerdefinierte Charaktere mit einzigartigen Namen, Beschreibungen und Erscheinungsbildern erstellen. Sie können auch benutzerdefinierte Welten und Themen erstellen. Melden Sie sich für ein kostenloses Konto an, um Ihre Kreationen zu speichern.",
  },
  faq4Question: {
    en: "How long does it take to create a story?",
    tr: "Bir hikaye oluşturmak ne kadar sürer?",
    de: "Wie lange dauert es, eine Geschichte zu erstellen?",
  },
  faq4Answer: {
    en: "Story generation typically takes 1-2 minutes. During this time, our AI writes the narrative, creates custom illustrations for each page, and can even generate background music and read-aloud narration.",
    tr: "Hikaye oluşturma genellikle 1-2 dakika sürer. Bu süre içinde yapay zekamız anlatımı yazar, her sayfa için özel illüstrasyonlar oluşturur ve hatta arka plan müziği ve sesli okuma üretebilir.",
    de: "Die Geschichtenerstellung dauert in der Regel 1-2 Minuten. In dieser Zeit schreibt unsere KI die Erzählung, erstellt benutzerdefinierte Illustrationen für jede Seite und kann sogar Hintergrundmusik und Vorleseerzählung generieren.",
  },
  faq5Question: {
    en: "Can I share or download the stories?",
    tr: "Hikayeleri paylaşabilir veya indirebilir miyim?",
    de: "Kann ich die Geschichten teilen oder herunterladen?",
  },
  faq5Answer: {
    en: "Absolutely! You can share your stories with a unique link that friends and family can view. You can also download your stories as PDF files to print or read offline.",
    tr: "Kesinlikle! Hikayelerinizi arkadaşlarınız ve ailenizin görüntüleyebileceği benzersiz bir bağlantıyla paylaşabilirsiniz. Ayrıca hikayelerinizi yazdırmak veya çevrimdışı okumak için PDF dosyaları olarak indirebilirsiniz.",
    de: "Absolut! Sie können Ihre Geschichten mit einem einzigartigen Link teilen, den Freunde und Familie ansehen können. Sie können Ihre Geschichten auch als PDF-Dateien herunterladen, um sie auszudrucken oder offline zu lesen.",
  },
  faq6Question: {
    en: "Is StorySpark free to use?",
    tr: "StorySpark ücretsiz mi?",
    de: "Ist StorySpark kostenlos?",
  },
  faq6Answer: {
    en: "StorySpark offers free story creation with a star-based system. You receive stars daily that can be used to create stories and access premium features. Creating an account is completely free!",
    tr: "StorySpark, yıldız tabanlı bir sistemle ücretsiz hikaye oluşturma sunar. Hikaye oluşturmak ve premium özelliklere erişmek için kullanabileceğiniz yıldızları günlük olarak alırsınız. Hesap oluşturmak tamamen ücretsiz!",
    de: "StorySpark bietet kostenlose Geschichtenerstellung mit einem Sterne-System. Sie erhalten täglich Sterne, die Sie zum Erstellen von Geschichten und für Premium-Funktionen verwenden können. Ein Konto zu erstellen ist völlig kostenlos!",
  },
};

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, replacements?: Record<string, string | number>) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState<Language>(() => {
    const saved = localStorage.getItem("language");
    return (saved as Language) || "tr";
  });

  const handleSetLanguage = (lang: Language) => {
    setLanguage(lang);
    localStorage.setItem("language", lang);
  };

  const t = (key: string, replacements?: Record<string, string | number>): string => {
    const translation = translations[key]?.[language] || translations[key]?.en || key;
    
    if (!replacements) return translation;
    
    return Object.entries(replacements).reduce(
      (str, [k, v]) => str.replace(`{${k}}`, String(v)),
      translation
    );
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage: handleSetLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}
